import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/firebase/api-client";
import { startOrderAlarm, stopOrderAlarm, jouerAlerteDemande } from "@/lib/utils/order-alarm";

/**
 * ===========================================================================
 *  CE QUE LES FOURNISSEURS CHANGENT EUX-MEMES
 * ===========================================================================
 *
 *  Horaires, conges, ouverture du service, retrait d'un article : le
 *  changement est deja applique quand cette ligne apparait. On ne fait pas
 *  attendre un fournisseur qui annonce son absence — le retenir ne
 *  l'empecherait pas d'etre absent, ca laisserait seulement des commandes
 *  partir chez quelqu'un qui n'est plus la.
 *
 *  Ce qui manquait, c'est que quelqu'un le VOIE, et qu'on sache QUI. D'ou
 *  le bouton, et le nom qu'il laisse derriere lui.
 *
 *  ---------------------------------------------------------------------
 *  LA FERMETURE EN PLEIN SERVICE EST A PART
 *  ---------------------------------------------------------------------
 *
 *  Fermer a l'heure prevue, c'est la fin de la journee : ca se lit le soir.
 *  Fermer a 17h58 quand on travaille jusqu'a 20h, c'est un rayon entier qui
 *  disparait du magasin sans que personne ne l'ait decide. Le support doit
 *  trancher tout de suite — desactiver la categorie, ou racheter en caisse.
 *
 *  Ces lignes-la CLIGNOTENT et SONNENT tant qu'elles ne sont pas validees.
 *  Le reste ne sonne pas : une alerte qui se declenche pour tout ne fait
 *  plus lever personne.
 * ===========================================================================
 */

type Demande = {
  id: string;
  nom: string;
  resume: string;
  type?: string;
  urgent?: boolean;
  valide: boolean;
  validePar?: string | null;
  creeLe?: string | null;
  payeCentimes?: number | null;
  /** Le responsable commande dont la caisse a paye (03/11/2026). */
  acheteur?: string | null;
  depenseId?: string | null;
  /** Le prix du fichier all product, a l'unite, en centimes. */
  refUniteCentimes?: number | null;
  quantite?: number | null;
  article?: string | null;
  /** Le magasin concerne. Le support voit les deux : sans lui, il valide a l'aveugle. */
  storeId?: string | null;
  /** Achat caisse deja paye : il se corrige (20/10/2026). */
  achete?: boolean;
  auPoids?: boolean;
  poidsKg?: number | null;
  prixKg?: number | null;
  corrigePar?: string | null;
};

/**
 * Une ligne fermee par une ANNULATION, pas par une validation.
 * Les deux boutons d'annulation ecrivent leur mention dans « validePar » :
 * c'est elle qui fait foi, l'ecran n'a pas a deviner.
 */
const estAnnulee = (d: { validePar?: string | null }) =>
  /^annul/i.test(String(d.validePar || ""));

/**
 * CE QU'ON VEUT LIRE D'UN COUP D'OEIL : « BOURGOGNE », pas
 * « NATURA BELDI - BOURGONE ». Les deux magasins portent le meme debut de
 * nom : ce debut n'apprend rien et repousse la seule partie qui distingue.
 */
const magasinCourt = (nom: string) => {
  const morceaux = String(nom).split(/\s*[-–—·|]\s*/).filter(Boolean);
  return (morceaux[morceaux.length - 1] || nom).trim();
};

/**
 * Fuseau fixe pour l'heure du Maroc (19/10/2026) : les navigateurs dont la
 * base de fuseaux precede le retour du Maroc a GMT avancent
 * « Africa/Casablanca » d'une heure. Meme regle que heure-maroc.ts.
 */
const FUSEAU_MAROC = "UTC";

const nombreTape = (v: string) => Number(String(v).replace(",", "."));

/** « 1 234,50 » — jamais « 1234.5 » sur un ecran qu'on lit vite. */
const dh = (centimes: number) =>
  (centimes / 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function DemandesFournisseurs({
  storeId,
}: {
  /** Le magasin regarde, ou « ALL » / undefined pour tout voir. */
  storeId?: string;
}) {
  const [demandes, setDemandes] = useState<Demande[]>([]);
  /** Vrai apres le premier chargement reussi : ce qui etait deja la ne sonne pas. */
  const [charge, setCharge] = useState(false);
  const [validationEnCours, setValidationEnCours] = useState<string | null>(null);
  /** La ligne en cours de correction, et ce qui est tape. */
  const [correction, setCorrection] = useState<{
    id: string;
    poids: string;
    prixKg: string;
    total: string;
  } | null>(null);
  const [erreurCorrection, setErreurCorrection] = useState<string | null>(null);
  /**
   * LE NOM DU MAGASIN, PAS SON IDENTIFIANT.
   * Le support voit les demandes des deux magasins dans la meme liste.
   * « Bouchaib demande a fermer » sans le magasin, c'est une decision prise
   * a l'aveugle : on desactive la categorie du mauvais magasin.
   */
  const [nomsMagasins, setNomsMagasins] = useState<Record<string, string>>({});
  /**
   * Le jour regarde, « AAAA-MM-JJ ». Vide = tous les jours. Par defaut
   * aujourd'hui : c'est ce qu'on vient voir en arrivant.
   */
  const [jourFiltre, setJourFiltre] = useState<string>(() =>
    new Date().toLocaleDateString("en-CA", { timeZone: FUSEAU_MAROC }),
  );

  // L'alarme n'appartient pas a ce composant : d'autres ecrans s'en
  // servent. On ne la coupe donc qu'en sortant, et seulement si c'est nous
  // qui l'avons lancee.
  const nousSonnons = useRef(false);

  // Les magasins ne changent pas dans la journee : on les lit une fois.
  useEffect(() => {
    let annule = false;
    void (async () => {
      try {
        const stores = await api.stores.getAll();
        if (annule) return;
        const table: Record<string, string> = {};
        for (const s of stores || []) {
          if (s?.id) table[String(s.id)] = String(s.name || s.id);
        }
        setNomsMagasins(table);
      } catch {
        /* Sans les noms on affiche l'identifiant : moins lisible, jamais faux. */
      }
    })();
    return () => {
      annule = true;
    };
  }, []);

  useEffect(() => {
    let annule = false;

    async function charger() {
      try {
        const r = await api.fournisseurs.demandes(
          !storeId || storeId === "ALL" ? undefined : storeId,
        );
        if (!annule) {
          setDemandes((r.demandes || []) as Demande[]);
          setCharge(true);
        }
      } catch {
        /* Le journal est un confort : son absence ne casse pas la page. */
      }
    }

    void charger();
    // UNE MINUTE A L'ECRAN, TROIS MINUTES ONGLET CACHE (25/10/2026).
    // Avant, la liste s'arretait onglet cache (14/10/2026, 1 200 relectures
    // par nuit) : une demande arrivee pendant qu'on regardait un autre
    // onglet ne sonnait jamais. Une alerte doit sonner onglet cache ; on
    // relit donc quand meme, mais trois fois moins souvent.
    let derniere = Date.now();
    const minuterie = window.setInterval(() => {
      const cache = typeof document !== "undefined" && document.visibilityState !== "visible";
      if (cache && Date.now() - derniere < 180000) return;
      derniere = Date.now();
      void charger();
    }, 60000);
    const surRetour = () => {
      if (document.visibilityState === "visible") {
        derniere = Date.now();
        void charger();
      }
    };
    document.addEventListener("visibilitychange", surRetour);
    return () => {
      annule = true;
      window.clearInterval(minuterie);
      document.removeEventListener("visibilitychange", surRetour);
    };
  }, [storeId]);

  // ---------------------------------------------------------------------
  //  LE SON DES NOUVELLES DEMANDES (25/10/2026)
  // ---------------------------------------------------------------------
  //  « On ne les remarque pas. » Chaque demande qui ARRIVE (pas encore
  //  validee, jamais vue par cet ecran) fait sonner « bip-bip-bip, boum »,
  //  un son a part, different de celui des commandes. Tant qu'il reste des
  //  demandes a voir, un rappel sonne toutes les 5 minutes, et le titre de
  //  l'onglet affiche le compte — visible meme onglet cache.
  const dejaVues = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!charge) return;
    const ouvertes = demandes.filter((d) => !d.valide).map((d) => d.id);
    if (dejaVues.current === null) {
      // Premier chargement : ce qui est deja la ne sonne pas.
      dejaVues.current = new Set(demandes.map((d) => d.id));
      return;
    }
    const nouvelles = ouvertes.filter((id) => !dejaVues.current!.has(id));
    demandes.forEach((d) => dejaVues.current!.add(d.id));
    if (nouvelles.length > 0) jouerAlerteDemande(3);
  }, [demandes, charge]);

  const nbAVoir = demandes.filter((d) => !d.valide).length;
  useEffect(() => {
    if (nbAVoir === 0) return;
    const rappel = window.setInterval(() => jouerAlerteDemande(1), 5 * 60 * 1000);
    return () => window.clearInterval(rappel);
  }, [nbAVoir > 0]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const base = document.title.replace(/^🔔 \(\d+\) /, "");
    document.title = nbAVoir > 0 ? `🔔 (${nbAVoir}) ${base}` : base;
    return () => {
      document.title = document.title.replace(/^🔔 \(\d+\) /, "");
    };
  }, [nbAVoir]);

  // TANT QUE CA N'EST PAS VALIDE, CA SONNE.
  //
  // L'alarme suit l'etat, elle ne se declenche pas une fois : quelqu'un qui
  // arrive dix minutes plus tard doit entendre la meme chose que celui qui
  // etait la. Elle s'arrete quand la derniere ligne urgente est validee —
  // personne n'a de bouton « silence », parce qu'un silence obtenu sans
  // decision est exactement ce qu'on veut eviter.
  const urgentes = demandes.filter((d) => d.urgent && !d.valide);

  useEffect(() => {
    if (urgentes.length > 0) {
      startOrderAlarm("admin");
      nousSonnons.current = true;
    } else if (nousSonnons.current) {
      stopOrderAlarm();
      nousSonnons.current = false;
    }
  }, [urgentes.length]);

  useEffect(() => {
    return () => {
      if (nousSonnons.current) stopOrderAlarm();
    };
  }, []);

  const valider = async (id: string, decision: "RETIRER" | "CAISSE" = "RETIRER") => {
    try {
      setValidationEnCours(id);
      const r = await api.fournisseurs.validerDemande(id, decision);
      setDemandes((liste) =>
        liste.map((d) =>
          d.id === id
            ? {
                ...d,
                valide: true,
                validePar: r.validePar,
              }
            : d,
        ),
      );
    } catch {
      /* En cas d'echec la ligne reste a valider : c'est la verite. */
    } finally {
      setValidationEnCours(null);
    }
  };

  /**
   * LE FOURNISSEUR A DECLARE UNE RUPTURE PAR ERREUR : l'article est la.
   *
   * Sans ce bouton, il n'y avait que deux issues, toutes deux fausses :
   * valider — et eteindre un article disponible — ou laisser la ligne
   * attendre, l'article restant marque indisponible indefiniment.
   *
   * Annuler n'eteint rien : l'article repasse en vente et la ligne se ferme
   * avec le nom de celui qui a annule.
   */
  const annulerRupture = async (d: Demande) => {
    if (
      !window.confirm(
        `Le fournisseur s'est trompé — l'article est disponible ?\n\n` +
          `« ${d.resume} »\n\n` +
          `Rien ne sera retiré : l'article repasse en vente.`,
      )
    )
      return;
    try {
      setValidationEnCours(d.id);
      const r = await api.fournisseurs.annulerDemande(d.id);
      setDemandes((l) =>
        l.map((x) =>
          x.id === d.id
            ? { ...x, valide: true, validePar: r.validePar || "annulé" }
            : x,
        ),
      );
    } catch (e: any) {
      // On dit POURQUOI. Un bouton qui ne fait rien, sans un mot, laisse
      // croire que c'est passe.
      alert(`Impossible d'annuler : ${e?.message || e}`);
    } finally {
      setValidationEnCours(null);
    }
  };

  /**
   * LE FOURNISSEUR S'EST TROMPE : il avait l'article. Meme geste que chez le
   * responsable commande — rien a acheter en caisse, l'article repart chez
   * lui, et la ligne se ferme avec le nom de celui qui a annule.
   */
  const annulerErreur = async (d: Demande) => {
    if (!d.depenseId) return;
    if (
      !window.confirm(
        "Le fournisseur a l'article ?\n\nL'achat en caisse est annulé et l'article redevient à préparer chez lui.",
      )
    )
      return;
    try {
      setValidationEnCours(d.id);
      await api.orders.soldierAchatCaisse(d.depenseId, { erreurFournisseur: true });
      setDemandes((l) =>
        l.map((x) =>
          x.id === d.id
            ? { ...x, valide: true, validePar: "annulé : erreur du fournisseur" }
            : x,
        ),
      );
    } catch (e: any) {
      alert(`Impossible d'annuler : ${e?.message || e}`);
    } finally {
      setValidationEnCours(null);
    }
  };

  /**
   * LE RESPONSABLE COMMANDE S'EST TROMPE EN SAISISSANT L'ACHAT (20/10/2026).
   * « 95 » tape pour 0,95 kg : 6 650 DH payes pour un poulet. Le support
   * remet le bon chiffre ; le serveur refait la depense, la pesee, le prix
   * client de la ligne et le total de la commande, et garde l'ancienne
   * valeur.
   */
  const ouvrirCorrection = (d: Demande) => {
    setErreurCorrection(null);
    setCorrection({
      id: d.id,
      poids: d.poidsKg ? String(d.poidsKg).replace(".", ",") : "",
      prixKg: d.prixKg ? String(d.prixKg).replace(".", ",") : "",
      total: d.payeCentimes ? String(d.payeCentimes / 100).replace(".", ",") : "",
    });
  };

  const enregistrerCorrection = async (d: Demande) => {
    if (!correction || !d.depenseId) return;
    setErreurCorrection(null);
    const corps: { poidsKg?: number; prixKg?: number; prixPayeDH?: number } = {};
    if (d.auPoids) {
      const poids = nombreTape(correction.poids);
      const prixKg = nombreTape(correction.prixKg);
      if (!(poids > 0)) return setErreurCorrection("Entre le poids en kilos (ex. 0,95).");
      if (!(prixKg > 0)) return setErreurCorrection("Entre le prix du kilo.");
      corps.poidsKg = poids;
      corps.prixKg = prixKg;
    } else {
      const total = nombreTape(correction.total);
      if (!(total > 0)) return setErreurCorrection("Entre le montant réellement payé.");
      corps.prixPayeDH = total;
    }
    try {
      setValidationEnCours(d.id);
      const r = await api.orders.corrigerAchatCaisse(d.depenseId, corps);
      setDemandes((l) =>
        l.map((x) =>
          x.id === d.id
            ? {
                ...x,
                payeCentimes: r.payeCentimes ?? x.payeCentimes,
                poidsKg: corps.poidsKg ?? x.poidsKg,
                prixKg: corps.prixKg ?? x.prixKg,
                corrigePar: r.corrigePar || x.corrigePar,
              }
            : x,
        ),
      );
      setCorrection(null);
    } catch (e: any) {
      setErreurCorrection(e?.message || "Correction impossible.");
    } finally {
      setValidationEnCours(null);
    }
  };

  const aVoir = demandes.filter((d) => !d.valide).length;

  /**
   * LE JOUR D'UNE DEMANDE, a Casablanca — « 2026-09-21 ».
   * Pas le jour de l'ordinateur : un poste regle sur Paris ferait basculer
   * a minuit la demande de 23 h 30.
   */
  const jourDe = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString("en-CA", {
          timeZone: FUSEAU_MAROC,
        })
      : "";
  const aujourdhui = jourDe(new Date().toISOString());

  /**
   * EN RETARD : pas validee, et deposee un jour precedent.
   * La demande de la veille qui attend encore est celle qu'on oublie — elle
   * porte donc sa date et un signe, et elle ne quitte jamais l'ecran.
   */
  const enRetard = (d: Demande) =>
    !d.valide && !!d.creeLe && jourDe(d.creeLe) < aujourdhui;
  const nbEnRetard = demandes.filter(enRetard).length;

  // LE FILTRE PAR JOUR. Il trie ce qui est TRAITE ; il ne cache jamais ce
  // qui attend. Une demande non validee d'hier reste en haut meme quand on
  // regarde aujourd'hui — sinon le filtre servirait precisement a ne plus
  // la voir.
  const visibles = demandes.filter(
    (d) => !jourFiltre || !d.valide || jourDe(d.creeLe) === jourFiltre,
  );

  // L'ORDRE : ce qui brule, ce qui traine, ce qui attend, ce qui est fait.
  //   0  fermeture de service a trancher
  //   1  en retard — pas validee depuis la veille ou avant
  //   2  a valider, du jour
  //   3  validee — en bas, elle a eu son lecteur
  const rang = (d: Demande) =>
    d.urgent && !d.valide ? 0 : enRetard(d) ? 1 : !d.valide ? 2 : 3;
  const tries = [...visibles].sort((a, b) => {
    const r = rang(a) - rang(b);
    if (r !== 0) return r;
    // A rang egal, la plus recente d'abord.
    return String(b.creeLe || "").localeCompare(String(a.creeLe || ""));
  });

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-5">
      {/* L'animation vit ici, pas dans la feuille de style globale : elle
          ne sert qu'a ce bloc, et on doit pouvoir la supprimer en
          supprimant le fichier. */}
      <style>{`
        @keyframes battement-alerte {
          0%, 100% { background-color: #fee2e2; border-color: #dc2626; }
          50%      { background-color: #fecaca; border-color: #7f1d1d; }
        }
        .ligne-urgente { animation: battement-alerte 1s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          /* Un ecran qui clignote peut etre insupportable a certains. La
             ligne reste rouge vif : c'est le rouge qui informe, le
             clignotement ne fait qu'attirer l'oeil. */
          .ligne-urgente { animation: none; background-color: #fecaca; }
        }
      `}</style>

      <div className="flex flex-wrap items-baseline gap-2 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Demandes des fournisseurs
        </p>
        {aVoir > 0 && (
          <span className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
            {aVoir} à voir
          </span>
        )}
        {urgentes.length > 0 && (
          <span className="ligne-urgente text-[11px] font-black uppercase tracking-wide text-red-900 border-2 rounded-full px-2.5 py-0.5">
            {urgentes.length} fermeture{urgentes.length > 1 ? "s" : ""} à
            trancher
          </span>
        )}
        {nbEnRetard > 0 && (
          <span className="text-[11px] font-bold text-orange-800 bg-orange-100 border border-orange-300 rounded-full px-2 py-0.5">
            ⚠ {nbEnRetard} en retard
          </span>
        )}

        {/* LE JOUR REGARDE. Les demandes non validees restent affichees
            quel que soit le jour choisi : le filtre range ce qui est
            traite, il ne cache jamais ce qui attend. */}
        <div className="ml-auto flex items-center gap-1.5">
          <input
            type="date"
            value={jourFiltre}
            max={aujourdhui}
            onChange={(e) => setJourFiltre(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white"
          />
          <button
            type="button"
            onClick={() => setJourFiltre(jourFiltre ? "" : aujourdhui)}
            className="text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg px-2 py-1 hover:bg-gray-50"
          >
            {jourFiltre ? "Tous les jours" : "Aujourd'hui"}
          </button>
        </div>
      </div>

      {demandes.length === 0 ? (
        <p className="text-sm text-gray-400">
          Aucun changement déclaré pour le moment.
        </p>
      ) : (
        <div className="space-y-2">
          {/* LES URGENTES D'ABORD, quel que soit leur age. Une fermeture de
              service enterree sous quinze changements d'horaires n'alerte
              personne. */}
          {tries.length === 0 && (
            <p className="text-sm text-gray-400">
              Rien ce jour-là — et rien en attente.
            </p>
          )}
          {tries.map((d) => {
              const urgente = d.urgent && !d.valide;
              const retard = enRetard(d);
              return (
                <div
                  key={d.id}
                  // flex-wrap : trois issues possibles sur une rupture, les
                  // boutons doivent pouvoir passer a la ligne plutot que
                  // deborder de l'ecran.
                  className={`flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between rounded-xl border px-4 py-3 ${
                    urgente
                      ? "ligne-urgente border-2"
                      : retard
                        ? "border-2 border-orange-400 bg-orange-50"
                        : d.valide
                          ? "border-gray-200 bg-gray-50"
                          : "border-amber-300 bg-amber-50"
                  }`}
                >
                  <div className="min-w-0">
                    {/* LE MAGASIN, EN PREMIER ET EN CLAIR.
                        Le support suit les deux magasins : la meme demande,
                        du meme fournisseur, ne se tranche pas pareil selon
                        qu'elle vient d'Oulfa ou de Bourgogne. */}
                    {d.storeId && (
                      <span className="inline-block mb-1 text-[11px] font-black uppercase tracking-wide text-white bg-slate-800 rounded px-2 py-0.5">
                        {nomsMagasins[d.storeId]
                          ? magasinCourt(nomsMagasins[d.storeId])
                          : d.storeId}
                      </span>
                    )}
                    <p
                      className={
                        urgente
                          ? "text-sm font-bold text-red-900"
                          : "text-sm text-gray-900"
                      }
                    >
                      <span className="font-bold">{d.nom}</span> {d.resume}
                    </p>

                    {/* CE QU'IL FAUT FAIRE, ecrit noir sur blanc.
                        « À valider » ne dit pas quoi faire ; ces deux
                        chemins-la, si. */}
                    {urgente && (
                      <p className="text-xs font-semibold text-red-800 mt-1">
                        À trancher maintenant : désactiver sa catégorie, ou
                        basculer ses articles en achat par la caisse.
                      </p>
                    )}

                    {/* LE TARIF DE REFERENCE, A COTE DU TARIF PAYE.
                        On ne valide pas un montant : on valide un ECART.
                        Sans le prix du fichier all product en face, « payé
                        100 DH » ne dit pas si le responsable commande a
                        acheté au bon tarif — et c'est justement ce qu'on
                        demande a la personne qui clique de verifier. */}
                    {(() => {
                      const unite = Number(d.refUniteCentimes) || 0;
                      const quantite = Number(d.quantite) || 1;
                      const paye = Number(d.payeCentimes) || 0;
                      if (!unite && !paye) return null;

                      const attendu = unite * quantite;
                      const ecart = attendu > 0 && paye > 0 ? paye - attendu : 0;
                      const pourcent =
                        attendu > 0 && paye > 0
                          ? Math.round((ecart / attendu) * 100)
                          : 0;
                      // Au-dela du tarif du fichier, la marge part : c'est
                      // le seul cas ou l'ecran doit crier.
                      const depasse = ecart > 0;

                      return (
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                          {unite > 0 ? (
                            <span className="text-gray-700">
                              tarif all product :{" "}
                              <strong>{dh(unite)} DH</strong>
                              {quantite > 1 && (
                                <>
                                  {" "}
                                  × {quantite} ={" "}
                                  <strong>{dh(attendu)} DH</strong>
                                </>
                              )}
                            </span>
                          ) : (
                            <span className="text-amber-800 font-semibold">
                              ⚠ aucun tarif all product pour cet article —
                              impossible de contrôler le prix
                            </span>
                          )}

                          {paye > 0 && (
                            <span
                              className={
                                depasse
                                  ? "font-bold text-rose-700"
                                  : "font-bold text-emerald-700"
                              }
                            >
                              payé en caisse
                              {d.acheteur && (
                                <>
                                  {" "}par <span className="underline decoration-dotted">{d.acheteur}</span>
                                </>
                              )}{" "}
                              : {dh(paye)} DH
                              {attendu > 0 && ecart !== 0 && (
                                <>
                                  {" "}
                                  ({ecart > 0 ? "+" : "−"}
                                  {dh(Math.abs(ecart))} DH
                                  {pourcent !== 0 &&
                                    `, ${ecart > 0 ? "+" : "−"}${Math.abs(pourcent)} %`}
                                  )
                                </>
                              )}
                              {attendu > 0 && ecart === 0 && " — au tarif"}
                            </span>
                          )}

                          {paye === 0 && unite > 0 && (
                            <span className="text-gray-500">
                              pas encore payé en caisse
                            </span>
                          )}
                        </div>
                      );
                    })()}

                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {/* L'HEURE DU MAGASIN, PAS CELLE DE L'ORDINATEUR : un
                          poste reglé sur Paris montrait 17h44 pour un
                          evenement de 16h44 a Casablanca. */}
                      {d.creeLe
                        ? new Date(d.creeLe).toLocaleString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZone: FUSEAU_MAROC,
                          })
                        : ""}
                      {/* LE SIGNE DU RETARD. La date est deja la ; ce qui
                          manquait, c'est de dire qu'elle est DEPASSEE. */}
                      {retard && (
                        <span className="text-orange-700 font-bold">
                          {" · ⚠ pas validée depuis la veille — à valider"}
                        </span>
                      )}
                      {d.corrigePar && (
                        <span className="text-indigo-700 font-semibold">
                          {" · corrigé par "}
                          {d.corrigePar}
                        </span>
                      )}
                      {d.valide && d.validePar ? (
                        // « vu par annulé par Amina » ne se lit pas. Une
                        // annulation porte deja son auteur dans sa mention.
                        <span
                          className={
                            estAnnulee(d)
                              ? "text-blue-700 font-semibold"
                              : "text-green-700 font-semibold"
                          }
                        >
                          {estAnnulee(d) ? " · " : " · vu par "}
                          {d.validePar}
                        </span>
                      ) : null}
                    </p>
                  </div>

                  {d.depenseId && d.achete && correction?.id !== d.id && (
                    <button
                      type="button"
                      disabled={validationEnCours === d.id}
                      onClick={() => ouvrirCorrection(d)}
                      className="shrink-0 rounded-lg px-3 py-2 text-xs font-bold text-indigo-700 border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50"
                    >
                      ✏️ Corriger le montant
                    </button>
                  )}
                  {d.depenseId && correction?.id === d.id && (
                    <div className="w-full rounded-xl border-2 border-indigo-300 bg-white p-3 space-y-2">
                      <p className="text-sm font-bold text-indigo-900">
                        Corriger l&apos;achat caisse
                        {d.article ? ` — ${d.article}` : ""}
                        {(d.quantite || 1) > 1 ? ` (× ${d.quantite})` : ""}
                      </p>
                      {d.auPoids ? (
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <label className="font-semibold">Poids réel (kg)</label>
                          <input
                            inputMode="decimal"
                            value={correction.poids}
                            onChange={(e) => setCorrection({ ...correction, poids: e.target.value })}
                            className="w-24 px-2 py-1.5 border-2 border-gray-300 rounded-lg text-center font-bold"
                            placeholder="0,95"
                          />
                          <span>×</span>
                          <input
                            inputMode="decimal"
                            value={correction.prixKg}
                            onChange={(e) => setCorrection({ ...correction, prixKg: e.target.value })}
                            className="w-24 px-2 py-1.5 border-2 border-gray-300 rounded-lg text-center font-bold"
                            placeholder="DH/kg"
                          />
                          <span>DH/kg + 2 DH/poulet =</span>
                          <strong>
                            {(() => {
                              const t =
                                nombreTape(correction.poids) * nombreTape(correction.prixKg) +
                                2 * Math.max(1, Number(d.quantite) || 1);
                              return Number.isFinite(t) && t > 0 ? `${dh(Math.round(t * 100))} DH` : "—";
                            })()}
                          </strong>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <label className="font-semibold">Total réellement payé (DH)</label>
                          <input
                            inputMode="decimal"
                            value={correction.total}
                            onChange={(e) => setCorrection({ ...correction, total: e.target.value })}
                            className="w-28 px-2 py-1.5 border-2 border-gray-300 rounded-lg text-center font-bold"
                          />
                        </div>
                      )}
                      {d.auPoids && (
                        <p className="text-[11px] text-gray-500">
                          Le prix client de la ligne et le total de la commande sont recalculés.
                        </p>
                      )}
                      {erreurCorrection && (
                        <p className="text-sm font-semibold text-red-700">{erreurCorrection}</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={validationEnCours === d.id}
                          onClick={() => void enregistrerCorrection(d)}
                          className="rounded-lg px-4 py-2 text-sm font-bold text-white bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50"
                        >
                          {validationEnCours === d.id ? "…" : "Enregistrer la correction"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCorrection(null)}
                          className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 border border-gray-200"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}
                  {!d.valide && d.type === "INDISPONIBLE" && d.depenseId && (
                    <button
                      type="button"
                      disabled={validationEnCours === d.id}
                      onClick={() => void annulerErreur(d)}
                      className="shrink-0 rounded-lg px-3 py-2 text-xs font-bold text-blue-700 border border-blue-300 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                    >
                      Erreur du fournisseur — il l&apos;a
                    </button>
                  )}
                  {/* LE FOURNISSEUR S'EST TROMPE.
                      Une rupture ou un retrait declare a tort n'avait aucune
                      sortie : valider aurait eteint un article disponible.
                      Ce bouton ne retire rien — il remet en vente. */}
                  {!d.valide &&
                    (d.type === "RUPTURE" || d.type === "SUPPRESSION_ARTICLE") && (
                      <button
                        type="button"
                        disabled={validationEnCours === d.id}
                        onClick={() => void annulerRupture(d)}
                        className="shrink-0 rounded-lg px-3 py-2 text-xs font-bold text-blue-700 border border-blue-300 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                      >
                        Erreur du fournisseur — il l&apos;a
                      </button>
                    )}
                  {d.valide ? (
                    // « validé » sur une ligne annulee serait un mensonge :
                    // rien n'a ete valide, au contraire.
                    estAnnulee(d) ? (
                      <span className="shrink-0 text-xs font-bold text-blue-700">
                        ✕ annulé — remis en vente
                      </span>
                    ) : (
                      <span className="shrink-0 text-xs font-bold text-green-700">
                        ✓ validé
                      </span>
                    )
                  ) : (
                    <>
                    {/* Une demande qui ETEINT des articles : deux issues. */}
                    {(d.type === "RUPTURE" ||
                      d.type === "SUPPRESSION_ARTICLE" ||
                      d.type === "CONGE" ||
                      (d.type === "SERVICE" && /demande à fermer|DEMANDE À FERMER/.test(d.resume))) && (
                      <button
                        type="button"
                        disabled={validationEnCours === d.id}
                        onClick={() => void valider(d.id, "CAISSE")}
                        className="shrink-0 rounded-lg px-3 py-2 text-xs font-bold text-amber-800 border border-amber-300 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
                      >
                        Garder en vente — achat caisse
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={validationEnCours === d.id}
                      onClick={() => void valider(d.id)}
                      className={`shrink-0 rounded-lg px-4 py-2 text-sm font-bold text-white disabled:opacity-50 ${
                        urgente
                          ? "bg-red-700 hover:bg-red-800"
                          : "bg-slate-900 hover:bg-slate-800"
                      }`}
                    >
                      {validationEnCours === d.id
                        ? "…"
                        : urgente
                          ? "J'ai tranché"
                          : "Valider"}
                    </button>
                    </>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
